/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-17
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  MasterItem,
  ChecklistMessage,
  ChecklistMessageInput,
  ParentResponse,
  ResponseSummary,
} from '../types/classEssentials';

/**
 * RTK Query API slice for Class Essentials feature
 */
export const classEssentialsApi = createApi({
  reducerPath: 'classEssentialsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['MasterItem', 'ChecklistMessage', 'ParentResponse'],
  endpoints: builder => ({
    // ===== MASTER ITEMS ENDPOINTS =====

    /**
     * Get all master items for a studio
     * GET /api/v1/class-essentials/master-items/?studio={studioId}
     */
    getMasterItems: builder.query<MasterItem[], string>({
      query: studioId => ({
        url: '/class-essentials/master-items/',
        params: { studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Ensure we always return an array
        if (Array.isArray(response)) {
          return response;
        }
        if (response?.results && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'MasterItem' as const,
                id,
              })),
              { type: 'MasterItem', id: 'LIST' },
            ]
          : [{ type: 'MasterItem', id: 'LIST' }],
    }),

    getMasterItem: builder.query<MasterItem, string>({
      query: id => `/class-essentials/master-items/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'MasterItem', id }],
    }),

    createMasterItem: builder.mutation<MasterItem, Partial<MasterItem>>({
      query: body => ({
        url: '/class-essentials/master-items/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'MasterItem', id: 'LIST' }],
    }),

    updateMasterItem: builder.mutation<
      MasterItem,
      { id: string; data: Partial<MasterItem> }
    >({
      query: ({ id, data }) => ({
        url: `/class-essentials/master-items/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MasterItem', id },
        { type: 'MasterItem', id: 'LIST' },
      ],
    }),

    deleteMasterItem: builder.mutation<void, string>({
      query: id => ({
        url: `/class-essentials/master-items/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'MasterItem', id },
        { type: 'MasterItem', id: 'LIST' },
      ],
    }),

    // Checklist Messages endpoints
    getChecklistMessages: builder.query<ChecklistMessage[], string>({
      query: studioId => ({
        url: '/class-essentials/messages/',
        params: { studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Ensure we always return an array
        if (Array.isArray(response)) {
          return response;
        }
        if (response?.results && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ChecklistMessage' as const,
                id,
              })),
              { type: 'ChecklistMessage', id: 'LIST' },
            ]
          : [{ type: 'ChecklistMessage', id: 'LIST' }],
    }),

    getChecklistMessage: builder.query<ChecklistMessage, string>({
      query: id => `/class-essentials/messages/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'ChecklistMessage', id }],
    }),

    createChecklistMessage: builder.mutation<
      ChecklistMessage,
      ChecklistMessageInput
    >({
      query: body => ({
        url: '/class-essentials/messages/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ChecklistMessage', id: 'LIST' }],
    }),

    updateChecklistMessage: builder.mutation<
      ChecklistMessage,
      { id: string; data: Partial<ChecklistMessageInput> }
    >({
      query: ({ id, data }) => ({
        url: `/class-essentials/messages/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ChecklistMessage', id },
        { type: 'ChecklistMessage', id: 'LIST' },
      ],
    }),

    deleteChecklistMessage: builder.mutation<void, string>({
      query: id => ({
        url: `/class-essentials/messages/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'ChecklistMessage', id },
        { type: 'ChecklistMessage', id: 'LIST' },
      ],
    }),

    sendChecklistMessage: builder.mutation<ChecklistMessage, string>({
      query: id => ({
        url: `/class-essentials/messages/${id}/send/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'ChecklistMessage', id },
        { type: 'ChecklistMessage', id: 'LIST' },
      ],
    }),

    // Public endpoint for parent view (no authentication required)
    getPublicChecklist: builder.query<ChecklistMessage, string>({
      query: token => ({
        url: `/class-essentials/public/${token}/`,
      }),
    }),

    // Submit parent responses (no authentication required)
    submitParentResponses: builder.mutation<
      any,
      {
        token: string;
        responses: Array<{
          item_selection_id: string;
          status: string;
          notes?: string;
        }>;
        recipient_email?: string;
        parent_name?: string;
        student_name?: string;
      }
    >({
      query: ({ token, ...body }) => ({
        url: `/class-essentials/public/${token}/respond/`,
        method: 'POST',
        body,
      }),
    }),

    // Get parent responses for a studio
    getParentResponses: builder.query<ParentResponse[], string>({
      query: studioId => ({
        url: '/class-essentials/responses/',
        params: { message__studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Ensure we always return an array
        if (Array.isArray(response)) {
          return response;
        }
        if (response?.results && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ParentResponse' as const,
                id,
              })),
              { type: 'ParentResponse', id: 'LIST' },
            ]
          : [{ type: 'ParentResponse', id: 'LIST' }],
    }),

    // Get aggregated response summary for studio
    getResponseSummary: builder.query<ResponseSummary, string>({
      query: studioId => ({
        url: '/class-essentials/responses/summary/',
        params: { message__studio: studioId },
      }),
      providesTags: [{ type: 'ParentResponse', id: 'SUMMARY' }],
    }),
  }),
});

export const {
  useGetMasterItemsQuery,
  useGetMasterItemQuery,
  useCreateMasterItemMutation,
  useUpdateMasterItemMutation,
  useDeleteMasterItemMutation,
  useGetChecklistMessagesQuery,
  useGetChecklistMessageQuery,
  useCreateChecklistMessageMutation,
  useUpdateChecklistMessageMutation,
  useDeleteChecklistMessageMutation,
  useSendChecklistMessageMutation,
  useGetPublicChecklistQuery,
  useSubmitParentResponsesMutation,
  useGetParentResponsesQuery,
  useGetResponseSummaryQuery,
} = classEssentialsApi;
